import type { EmailAccountListItem } from '@/lib/email-accounts';
import { isAccountSending } from './account-status';
import type { SetupItem } from './setup-score';

/**
 * Outreach Readiness derivation + the checklist-percent helper. Pure,
 * non-component module — sibling to `account-status.ts`.
 *
 * The accounts table's Setup Score is NOT derived here: it renders the stored
 * `setupScore` (`emailAccountDomainScores.totalScore`) verbatim, because that
 * column is what the `setupScoreMin`/`setupScoreMax` filter matches. A former
 * client-side checklist (`deriveSetupScoreItems`, equally weighted) showed a
 * different number — e.g. 83% for 5 of 6 items — than the weighted stored
 * score the filter ran against, so the filter looked broken.
 */

/** Percent of items that are `ok` (0..100). Only `ok` counts toward
 * "complete" — `warning`/`unknown` don't. Extracted from the formula that
 * used to live inline in `EmailAccountSetupScore.tsx`. */
export function setupScorePercent(items: SetupItem[]): number {
  if (items.length === 0) return 0;
  const okCount = items.filter((i) => i.status === 'ok').length;
  return (okCount / items.length) * 100;
}

/*
 * `deriveSetupAuthItems` used to live here and merged `dnsRows(account)` with a
 * Sender-name row for the detail-page card. It is gone: `SetupAuthCard` now
 * renders the server's `domainSecurity.fields[]` directly (bands + record
 * values + per-row popup), so translating them through `SetupItemStatus` — a
 * three-state type that cannot carry "we couldn't check" — would have thrown
 * away the distinction the tri-state exists for. The card owns its own
 * Sender-name row now.
 */

export type OutreachReadinessStatus = 'ready' | 'warming' | 'not_ready';

/**
 * Status → display label, Title Case throughout (matches the table cell and
 * every other Title Case status, e.g. `STATUS_BUCKET_LABEL` in
 * `account-status.ts`). Single source for the outreach-readiness chip's own
 * label (`OutreachReadinessCell` in `tanstack/cells-derived.tsx`) and the
 * filter drawer's option list / active-filter chip (`active-filters.ts`,
 * `filters/filter-helpers.ts`) — these previously drifted ("Not Ready" vs
 * "Not ready").
 */
export const OUTREACH_READINESS_LABEL: Record<OutreachReadinessStatus, string> = {
  ready: 'Ready',
  warming: 'Warming',
  not_ready: 'Not Ready',
};

/*
 * Outreach Readiness thresholds. A hand-kept twin of
 * apps/api/src/modules/email-accounts/constants/outreach-readiness.constants.ts
 * (`@trulyinbox/types` is not a web dependency, so they cannot be shared). The
 * API runs the same rule as SQL for the `?outreachReadiness=` filter and the
 * CSV export (`outreachReadinessSql`). Change all three together.
 */
/** Inclusive: 90 is Ready. */
export const OUTREACH_READY_MIN_WARMUP_SCORE = 90;
/** Inclusive: 50 is Not Ready. */
export const OUTREACH_NOT_READY_MAX_WARMUP_SCORE = 50;

const NO_SCORE_REASON = 'No warmup score yet';

const scoreReason = (score: number): string =>
  `Warmup score ${score} (needs ${OUTREACH_READY_MIN_WARMUP_SCORE}+)`;

/**
 * Whether an account is safe to use for real outreach sending, graded on two
 * things only:
 *
 * 1. Is warmup ON? Exactly the table toggle's own predicate, `isAccountSending`
 *    (connected, and `warmupStatus` RUNNING / THROTTLED / MAINTENANCE), so the
 *    chip can never disagree with the switch beside it. Off (paused,
 *    suspended, not connected, disconnected, never started) reads `not_ready`.
 * 2. The latest warmup score (`latestWarmupScore`, the Reputation chart's
 *    newest point in the last 30 days): no score or 50 or less is
 *    `not_ready`, 51-89 `warming`, 90+ `ready`.
 *
 * `reasons` (the chip's hover text) always names the score, in every state.
 *
 * Nothing else plays a part: DNS, throttling, MAINTENANCE and the 15-day
 * `deliverabilityRate` are all ignored. The API's SQL twin
 * (`outreachReadinessSql`) reads the same `warmupStatus` mirror and status.
 */
export function deriveOutreachReadiness(
  a: EmailAccountListItem,
): { status: OutreachReadinessStatus; reasons: string[] } {
  // `undefined` on bulk-job/import rows is the same fact as `null`: no score.
  const latest = a.latestWarmupScore ?? null;

  if (!isAccountSending(a)) {
    return {
      status: 'not_ready',
      reasons: [latest === null ? NO_SCORE_REASON : `Warmup score ${latest.warmupScore} (warmup is off)`],
    };
  }
  if (latest === null) return { status: 'not_ready', reasons: [NO_SCORE_REASON] };

  const { warmupScore } = latest;
  if (warmupScore <= OUTREACH_NOT_READY_MAX_WARMUP_SCORE) {
    return { status: 'not_ready', reasons: [scoreReason(warmupScore)] };
  }
  if (warmupScore < OUTREACH_READY_MIN_WARMUP_SCORE) {
    return { status: 'warming', reasons: [scoreReason(warmupScore)] };
  }
  return { status: 'ready', reasons: [`Warmup score ${warmupScore}`] };
}
